"use client";

import type { FC } from "react";
import { ArrowLeft, Globe2, LockKeyhole } from "lucide-react";
import { useTranslations } from "next-intl";
import {
   Button,
   DialogDescription,
   DialogHeader,
   DialogTitle,
   Loading,
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue
} from "@/app/components/ui";
import { SHARE_ACCESS, SHARE_ACCESS_OPTIONS } from "@/app/lib/constants";
import type { ShareAccess } from "@/types";

interface ShareSettingsViewProps {
   access: ShareAccess;
   isShared: boolean;
   disabled: boolean;
   isStopping: boolean;
   onBack: () => void;
   onAccessChange: (access: ShareAccess) => void;
   onStopSharing: () => void;
}

export const ShareSettingsView: FC<ShareSettingsViewProps> = ({
   access,
   isShared,
   disabled,
   isStopping,
   onBack,
   onAccessChange,
   onStopSharing
}) => {
   const translate = useTranslations("Home");

   return (
      <>
         <DialogHeader className="flex-row items-center gap-2 space-y-0 text-left">
            <Button
               type="button"
               variant="ghost"
               size="icon"
               onClick={onBack}
               aria-label={translate("shareBack")}
               className="rounded-xl hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
            >
               <ArrowLeft size={18} />
            </Button>
            <DialogTitle className="text-xl font-semibold text-foreground">
               {translate("shareSettings")}
            </DialogTitle>
            <DialogDescription className="sr-only">
               {translate("shareDialogDescription")}
            </DialogDescription>
         </DialogHeader>

         <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">
               {translate("whoHasAccess")}
            </p>
            <Select
               value={access}
               onValueChange={(value) => onAccessChange(value as ShareAccess)}
               disabled={disabled}
            >
               <SelectTrigger className="h-auto min-h-11 whitespace-normal py-2 text-left [&>span]:line-clamp-none">
                  <SelectValue />
               </SelectTrigger>
               <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                  {Object.values(SHARE_ACCESS).map((value) => {
                     const option = SHARE_ACCESS_OPTIONS[value];
                     const OptionIcon =
                        value === SHARE_ACCESS.LINK ? Globe2 : LockKeyhole;
                     return (
                        <SelectItem key={value} value={value}>
                           <span className="flex items-center gap-3 text-left">
                              <OptionIcon
                                 size={18}
                                 className="shrink-0 text-muted-foreground"
                              />
                              <span>
                                 <span className="block text-sm font-medium">
                                    {translate(option.labelKey)}
                                 </span>
                                 <span className="block whitespace-normal text-xs text-muted-foreground">
                                    {translate(option.descriptionKey)}
                                 </span>
                              </span>
                           </span>
                        </SelectItem>
                     );
                  })}
               </SelectContent>
            </Select>
         </div>

         <div className="flex flex-col gap-3 rounded-xl border border-border p-4 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1">
               <p className="text-sm font-medium text-foreground">
                  {translate("stopSharing")}
               </p>
               <p className="text-sm text-muted-foreground">
                  {translate("stopSharingDescription")}
               </p>
            </div>
            <Button
               type="button"
               variant="destructive"
               onClick={onStopSharing}
               disabled={disabled || !isShared}
               className="rounded-xl"
            >
               {isStopping ? <Loading size="sm" /> : translate("stopSharing")}
            </Button>
         </div>
      </>
   );
};
